import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Handshake, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Auto-rotating Sponsor Spotlight for the homepage.
 * Highlights one real sponsor at a time (5s rotation) with a clean logo card,
 * dots for manual navigation, and a CTA to the full sponsors page.
 * Hides itself if there are no sponsors yet.
 */
const ROTATION_MS = 5000;

export default function SponsorSpotlight() {
    const [sponsors, setSponsors] = useState(null);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        api.get("/sponsors")
            .then((r) => setSponsors(r.data || []))
            .catch(() => setSponsors([]));
    }, []);

    // Prefer sponsors with a real uploaded logo (object-storage URL).
    // Filters out the legacy unsplash/pexels placeholders so the spotlight
    // only highlights real partners. Order: platinum → gold → partner.
    const items = useMemo(() => {
        if (!sponsors) return [];
        const rank = { platinum: 0, gold: 1, partner: 2 };
        return [...sponsors]
            .filter((s) => !!s.logo_url && s.logo_url.startsWith("/api/files/"))
            .sort((a, b) => (rank[a.tier] ?? 9) - (rank[b.tier] ?? 9));
    }, [sponsors]);

    useEffect(() => {
        if (paused || items.length <= 1) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATION_MS);
        return () => clearInterval(t);
    }, [items.length, paused]);

    if (sponsors === null || items.length === 0) return null;
    const current = items[idx % items.length];

    return (
        <section
            className="relative border-y border-line overflow-hidden bg-bg-soft"
            data-testid="sponsor-spotlight"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Faint backdrop */}
            <div className="absolute inset-0 opacity-40 bg-gradient-to-r from-bg via-purple-dark/40 to-bg" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
                <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center">
                    {/* Left: label + name + CTA */}
                    <div className="md:col-span-5">
                        <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-3 flex items-center gap-2">
                            <Handshake size={14} /> Royal Court · Sponsor Spotlight
                        </div>
                        <div className="min-h-[120px]">
                            <AnimatePresence mode="wait">
                                <motion.h2
                                    key={current.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    transition={{ duration: 0.45 }}
                                    className="h-display text-4xl md:text-5xl text-white leading-[0.95]"
                                    data-testid="spotlight-name"
                                >
                                    {current.name}
                                </motion.h2>
                            </AnimatePresence>
                            <div className="mt-2 text-ink text-xs tracking-[0.3em] uppercase">
                                {current.tier ? `${current.tier} sponsor` : "Sponsor"}
                            </div>
                        </div>

                        <p className="text-white/70 text-base mt-5 leading-relaxed max-w-md">
                            The kingdom runs because our partners back the throne. Join the court — your brand alongside the Kings.
                        </p>

                        <div className="mt-6 flex flex-wrap items-center gap-6">
                            <Link
                                to="/sponsors"
                                data-testid="spotlight-all-sponsors"
                                className="btn-gold px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2"
                            >
                                All Partners <ArrowRight size={14} />
                            </Link>

                            {/* Manual dots */}
                            <div className="flex gap-2" data-testid="spotlight-dots">
                                {items.map((s, i) => (
                                    <button
                                        key={s.id}
                                        aria-label={`Show ${s.name}`}
                                        onClick={() => setIdx(i)}
                                        data-testid={`spotlight-dot-${i}`}
                                        className={`h-1.5 transition-all ${i === idx ? "w-8 bg-gold" : "w-3 bg-line hover:bg-white/40"}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: rotating logo card */}
                    <div className="md:col-span-7">
                        <a
                            href={current.website || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="block"
                        >
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={current.id}
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    className="aspect-[16/9] w-full bg-white border border-gold/30 grid place-items-center p-10 md:p-16 hover:border-gold transition-colors"
                                    data-testid="spotlight-logo-card"
                                >
                                    <img
                                        src={current.logo_url}
                                        alt={current.name}
                                        className="max-h-full max-w-full object-contain"
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
